// "The user is in a sheet/screen owned by another app or by the OS" — Google or
// Apple sign-in, the store purchase sheet, the file picker, the share sheet.
// Those pause (and may stop) our activity, but the user hasn't left the app.
// On Android the seasonal icon switch disables the launch component, and the
// system then finishes the running activity: doing that mid-flow would lose the
// sign-in result, the photo, or the purchase UI. seasonIcon skips while any flow
// is open.
const DEFAULT_TIMEOUT_MS = 10 * 60 * 1000; // safety net for a flow whose end we never see

let open = 0;
const timers = new Set();

// Returns an idempotent `end()`. The flow also ends by itself after timeoutMs.
export function beginExternalFlow({ timeoutMs = DEFAULT_TIMEOUT_MS } = {}) {
  open += 1;
  let ended = false;
  const end = () => {
    if (ended) return;
    ended = true;
    clearTimeout(timer);
    timers.delete(timer);
    open = Math.max(0, open - 1);
  };
  const timer = setTimeout(end, timeoutMs);
  timers.add(timer);
  return end;
}

export function externalFlowActive() {
  return open > 0;
}

export async function withExternalFlow(fn) {
  const end = beginExternalFlow();
  try {
    return await fn();
  } finally {
    end();
  }
}

// <input type="file"> opens the picker declaratively, so it can't be wrapped at
// each call site: watch clicks on file inputs at the document (capture phase,
// which also sees the non-bubbling `cancel`) and end on change/cancel.
export function trackFilePickers(doc = document) {
  let end = null;
  const isFileInput = (t) => t && t.tagName === "INPUT" && t.type === "file";
  // A new click always starts a fresh flow (ending any previous one): a handle
  // already ended by the safety timeout must not block guarding later pickers.
  const onClick = (e) => {
    if (!isFileInput(e.target)) return;
    if (end) end();
    end = beginExternalFlow();
  };
  const onDone = (e) => { if (isFileInput(e.target) && end) { end(); end = null; } };
  doc.addEventListener("click", onClick, true);
  doc.addEventListener("change", onDone, true);
  doc.addEventListener("cancel", onDone, true);
  return () => {
    doc.removeEventListener("click", onClick, true);
    doc.removeEventListener("change", onDone, true);
    doc.removeEventListener("cancel", onDone, true);
    if (end) { end(); end = null; }
  };
}

export function __resetExternalFlows() {
  timers.forEach((t) => clearTimeout(t));
  timers.clear();
  open = 0;
}
